import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { formatDistanceToNowStrict } from "date-fns";
import { clsx } from "clsx";
import { api } from "../lib/api";
import { useBetaDesign } from "../lib/platform";
import type { NotificationItem } from "../lib/types";

// Floating bell button + inbox. The button itself is always the
// same (top-right corner of the layout); the inbox rendering
// branches on beta-design:
//   - Default: a small popover anchored under the button.
//   - Beta (iOS native): a full-screen sheet with a large title,
//     Done button, and inset-grouped list rows — same pattern as
//     IOSNativeMenu so the two surfaces feel like one app.
export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const qc = useQueryClient();
  const betaDesign = useBetaDesign();

  const list = useQuery({
    queryKey: ["notifications"],
    queryFn: api.listNotifications,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });

  const markOne = useMutation({
    mutationFn: (id: string) => api.markNotificationRead(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications"] }),
  });
  const markAll = useMutation({
    mutationFn: () => api.markAllNotificationsRead(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications"] }),
  });

  // Click-outside dismisses the popover. The button itself is
  // excluded so clicking it again closes (rather than reopening).
  // Skipped in beta — the full-screen sheet covers everything,
  // there's no "outside" to click except the Done button.
  useEffect(() => {
    if (!open || betaDesign) return;
    function onClick(e: MouseEvent) {
      const t = e.target as Node;
      if (popoverRef.current?.contains(t)) return;
      if (buttonRef.current?.contains(t)) return;
      setOpen(false);
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open, betaDesign]);

  // Esc closes — keyboard parity with most popovers on the
  // platform. On iOS this rarely fires but is harmless.
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  const items = list.data ?? [];
  const unread = items.filter((n) => !n.readAt).length;

  function close() {
    setOpen(false);
  }

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => {
          setOpen((o) => !o);
          // Immediate refetch on open so the list is current — the
          // 60s poll keeps the badge close to live but the user
          // expects the popover content to reflect "right now".
          if (!open) void list.refetch();
        }}
        className="print:hidden absolute right-3 top-2 z-40 w-9 h-9 flex items-center justify-center rounded-md bg-white/95 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 shadow-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700"
        aria-label={unread > 0 ? `${unread} unread notifications` : "Notifications"}
      >
        <BellIcon />
        {unread > 0 && (
          <span
            className="absolute -top-1 -right-1 min-w-[1.1rem] h-[1.1rem] px-1 flex items-center justify-center rounded-full bg-red-600 text-white text-[10px] font-semibold leading-none border border-white dark:border-slate-900"
            aria-hidden
          >
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {open && betaDesign && (
        <IOSNativeInbox
          items={items}
          loading={list.isLoading}
          unread={unread}
          onClose={close}
          onMarkOne={(id) => markOne.mutate(id)}
          onMarkAll={() => markAll.mutate()}
          markAllPending={markAll.isPending}
        />
      )}

      {open && !betaDesign && (
        <div
          ref={popoverRef}
          className="absolute right-3 top-12 z-50 w-[min(22rem,calc(100vw-1.5rem))] max-h-[min(32rem,calc(100vh-6rem))] flex flex-col bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-xl"
        >
          <div className="px-4 py-2.5 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-slate-800 dark:text-slate-100">
              Notifications
            </h2>
            {unread > 0 && (
              <button
                onClick={() => markAll.mutate()}
                disabled={markAll.isPending}
                className="text-xs text-sky-700 dark:text-sky-300 hover:underline disabled:opacity-50"
              >
                Mark all read
              </button>
            )}
          </div>

          <div className="flex-1 overflow-y-auto">
            {list.isLoading ? (
              <div className="px-4 py-6 text-center text-xs text-slate-500">
                Loading…
              </div>
            ) : items.length === 0 ? (
              <div className="px-4 py-8 text-center">
                <div className="text-3xl text-slate-300 dark:text-slate-600">⊘</div>
                <div className="mt-2 text-xs text-slate-500">
                  No notifications yet.
                </div>
              </div>
            ) : (
              <ul>
                {items.map((n) => (
                  <NotificationRow
                    key={n.id}
                    item={n}
                    onMarkRead={() => {
                      if (!n.readAt) markOne.mutate(n.id);
                    }}
                  />
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </>
  );
}

// Full-screen iOS-style notifications sheet rendered when the
// beta-design flag is on. Mirrors IOSNativeMenu's pattern: large
// title nav bar, Done button on the right, inset-grouped rounded
// cards with comfortable vertical spacing. Tapping a notification
// opens it full-screen for reading; exiting the detail view also
// closes the inbox (so you go straight back to the page you were
// on, not back to the list).
function IOSNativeInbox({
  items,
  loading,
  unread,
  onClose,
  onMarkOne,
  onMarkAll,
  markAllPending,
}: {
  items: NotificationItem[];
  loading: boolean;
  unread: number;
  onClose: () => void;
  onMarkOne: (id: string) => void;
  onMarkAll: () => void;
  markAllPending: boolean;
}) {
  // null = list view; otherwise = full-screen detail of the
  // selected notification. Keep the id (not the object) so the
  // detail re-resolves against the latest server state on the
  // next list refresh.
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const selected = selectedId
    ? items.find((n) => n.id === selectedId) ?? null
    : null;

  if (selected) {
    return (
      <IOSNotificationDetail
        item={selected}
        // Closing the detail dismisses the whole inbox per the
        // requested behavior — no intermediate hop back to the
        // list. Reset selectedId first so subsequent opens of
        // the inbox land on the list, not the stale detail.
        onClose={() => {
          setSelectedId(null);
          onClose();
        }}
      />
    );
  }

  return (
    <div
      className="fixed inset-0 z-[60] bg-slate-100 dark:bg-slate-950 flex flex-col"
      style={{
        paddingTop: "env(safe-area-inset-top)",
        paddingBottom: "env(safe-area-inset-bottom)",
      }}
    >
      <header className="px-4 pt-2 pb-3 flex items-end justify-between gap-2">
        <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-50">
          Notifications
        </h1>
        <button
          type="button"
          onClick={onClose}
          className="text-[17px] text-sky-600 dark:text-sky-400 font-normal py-1 -mr-1 px-1"
        >
          Done
        </button>
      </header>

      {unread > 0 && (
        <div className="px-4 pb-3 -mt-1">
          <button
            type="button"
            onClick={onMarkAll}
            disabled={markAllPending}
            className="text-[15px] text-sky-600 dark:text-sky-400 disabled:opacity-50"
          >
            Mark all as read ({unread})
          </button>
        </div>
      )}

      <div className="flex-1 overflow-y-auto px-4 pb-6">
        {loading ? (
          <div className="text-center py-12 text-[15px] text-slate-500 dark:text-slate-400">
            Loading…
          </div>
        ) : items.length === 0 ? (
          <div className="text-center pt-16">
            <div className="text-5xl text-slate-300 dark:text-slate-700">⊘</div>
            <div className="mt-3 text-[17px] text-slate-500 dark:text-slate-400">
              No notifications yet
            </div>
            <div className="mt-1 text-[13px] text-slate-400 dark:text-slate-500">
              Alerts sent to you will appear here.
            </div>
          </div>
        ) : (
          // space-y instead of divide-y so each notification reads
          // as its own card with breathing room between them. The
          // shadow + rounded corners on each card reinforce the
          // separation visually.
          <ul className="space-y-2.5">
            {items.map((n) => (
              <li
                key={n.id}
                className="bg-white dark:bg-slate-800 rounded-xl overflow-hidden shadow-sm"
              >
                <IOSNotificationRow
                  item={n}
                  onOpen={() => {
                    if (!n.readAt) onMarkOne(n.id);
                    setSelectedId(n.id);
                  }}
                />
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function IOSNotificationRow({
  item,
  onOpen,
}: {
  item: NotificationItem;
  onOpen: () => void;
}) {
  const unread = !item.readAt;
  return (
    <button
      onClick={onOpen}
      className="w-full text-left px-4 py-4 active:bg-slate-100 dark:active:bg-slate-700"
    >
      <div className="flex items-start gap-2.5">
        {/* Unread dot — iOS apps use a small filled circle on
          * the leading edge of each unread row. */}
        <span
          className={clsx(
            "mt-2 w-2.5 h-2.5 rounded-full shrink-0",
            unread ? "bg-sky-500" : "bg-transparent",
          )}
          aria-label={unread ? "unread" : undefined}
        />
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline justify-between gap-2">
            <div
              className={clsx(
                "text-[15px] truncate",
                unread
                  ? "font-semibold text-slate-900 dark:text-slate-50"
                  : "text-slate-800 dark:text-slate-100",
              )}
            >
              {item.subject}
            </div>
            <div className="text-[13px] text-slate-500 dark:text-slate-400 whitespace-nowrap shrink-0">
              {timeAgo(item.createdAt)}
            </div>
          </div>
          <div className="mt-1 text-[14px] text-slate-600 dark:text-slate-400 line-clamp-2 whitespace-pre-wrap leading-snug">
            {item.body}
          </div>
          {/* Chevron hint that the row is tappable into a detail
            * view. Subtle so it doesn't compete with the unread
            * dot for attention. */}
          <div className="mt-2 flex items-center justify-between">
            {(item.emailed || item.pushed) ? (
              <div className="flex gap-1.5">
                {item.emailed && <DeliveryTag label="Email" />}
                {item.pushed && <DeliveryTag label="Push" />}
              </div>
            ) : <span />}
            <span className="text-slate-300 dark:text-slate-600 text-lg leading-none">
              ›
            </span>
          </div>
        </div>
      </div>
    </button>
  );
}

// Detail screen for a single notification — full subject as the
// large title, metadata strip, and the body rendered with line
// breaks preserved. Closing returns all the way out (closes the
// inbox too) per the requested behavior.
function IOSNotificationDetail({
  item,
  onClose,
}: {
  item: NotificationItem;
  onClose: () => void;
}) {
  const ts = (() => {
    try {
      return new Date(item.createdAt).toLocaleString(undefined, {
        weekday: "short",
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      });
    } catch { return ""; }
  })();
  return (
    <div
      className="fixed inset-0 z-[70] bg-slate-100 dark:bg-slate-950 flex flex-col"
      style={{
        paddingTop: "env(safe-area-inset-top)",
        paddingBottom: "env(safe-area-inset-bottom)",
      }}
    >
      <header className="px-4 pt-2 pb-3 flex items-center justify-end">
        <button
          type="button"
          onClick={onClose}
          className="text-[17px] text-sky-600 dark:text-sky-400 font-normal py-1 -mr-1 px-1"
        >
          Done
        </button>
      </header>

      <div className="flex-1 overflow-y-auto px-4 pb-6">
        <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-50 leading-tight">
          {item.subject}
        </h1>
        <div className="mt-1 text-[13px] text-slate-500 dark:text-slate-400">
          {ts}
        </div>

        {(item.emailed || item.pushed) && (
          <div className="mt-3 flex gap-1.5">
            {item.emailed && <DeliveryTag label="Email" />}
            {item.pushed && <DeliveryTag label="Push" />}
          </div>
        )}

        <div className="mt-5 bg-white dark:bg-slate-800 rounded-xl p-4 shadow-sm">
          <p className="text-[16px] text-slate-800 dark:text-slate-100 whitespace-pre-wrap leading-relaxed">
            {item.body}
          </p>
        </div>
      </div>
    </div>
  );
}

function NotificationRow({
  item,
  onMarkRead,
}: {
  item: NotificationItem;
  onMarkRead: () => void;
}) {
  const unread = !item.readAt;
  return (
    <li>
      <button
        onClick={onMarkRead}
        className={clsx(
          "w-full text-left px-4 py-3 border-b border-slate-100 dark:border-slate-700 transition",
          "hover:bg-slate-50 dark:hover:bg-slate-700/60",
          unread && "bg-sky-50/60 dark:bg-sky-900/20",
        )}
      >
        <div className="flex items-start gap-2">
          {unread && (
            <span
              className="mt-1.5 w-2 h-2 rounded-full bg-sky-500 shrink-0"
              aria-label="unread"
            />
          )}
          <div className="min-w-0 flex-1">
            <div className="flex items-baseline justify-between gap-2">
              <div
                className={clsx(
                  "text-sm truncate",
                  unread
                    ? "font-semibold text-slate-900 dark:text-slate-50"
                    : "text-slate-700 dark:text-slate-200",
                )}
              >
                {item.subject}
              </div>
              <div className="text-[10px] text-slate-500 dark:text-slate-400 whitespace-nowrap shrink-0">
                {timeAgo(item.createdAt)}
              </div>
            </div>
            <div className="mt-0.5 text-xs text-slate-600 dark:text-slate-400 line-clamp-2 whitespace-pre-wrap">
              {item.body}
            </div>
            {(item.emailed || item.pushed) && (
              <div className="mt-1.5 flex gap-1.5">
                {item.emailed && <DeliveryTag label="Email" />}
                {item.pushed && <DeliveryTag label="Push" />}
              </div>
            )}
          </div>
        </div>
      </button>
    </li>
  );
}

function DeliveryTag({ label }: { label: string }) {
  return (
    <span className="text-[9px] uppercase tracking-wider font-medium text-slate-500 dark:text-slate-400 bg-slate-100 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded px-1 py-0.5">
      {label}
    </span>
  );
}

function timeAgo(iso: string): string {
  try {
    return formatDistanceToNowStrict(new Date(iso), { addSuffix: false });
  } catch {
    return "";
  }
}

function BellIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
      <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
    </svg>
  );
}
